<script setup>
import { reactive, computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useRouter } from 'vue-router';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import WhatsAppCampaignLimitsWidget from 'dashboard/components-next/Campaigns/WhatsAppCampaignLimitsWidget.vue';
import { useStore } from 'vuex';
import CampaignAPI from 'dashboard/api/campaigns';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
};

const initialState = {
  title: '',
  inboxId: null,
  templateId: null,
  scheduledAt: null,
  selectedAudience: [],
  delayBetweenMessages: 3,
};

const state = reactive({ ...initialState });
const templateParserRef = ref(null);

const limitsState = reactive({
  isLoading: false,
  data: {
    plan_available: 0,
    meta_daily_limit: 0,
    sends_today: 0,
    remaining_today: 0
  },
  audienceCount: 0,
  isFetchingAudience: false
});

const rules = {
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  templateId: { required },
  scheduledAt: { required },
  selectedAudience: { required },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = formState.getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    // Create a more user-friendly label from template name
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  template: getErrorMessage('templateId', 'TEMPLATE'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
  delayBetweenMessages: '', // Not strictly required with a default, but good to have
}));

const hasRequiredTemplateParams = computed(() => {
  if (!selectedTemplate.value) return false;
  return templateParserRef.value ? templateParserRef.value.isFormInvalid === false : false;
});

const remainingMessages = computed(() => {
  if (!limitsState.data) return 0;
  
  // Use the minimum of plan available and meta daily remaining, if they exist
  // We cannot use || Infinity because 0 || Infinity === Infinity
  const plan = limitsState.data.plan_available !== undefined && limitsState.data.plan_available !== null
    ? limitsState.data.plan_available 
    : Infinity;
    
  let meta = limitsState.data.remaining_today !== undefined && limitsState.data.remaining_today !== null
    ? limitsState.data.remaining_today 
    : Infinity;
    
  // -1 means unlimited in meta
  if (meta === -1) {
    meta = Infinity;
  }
  
  return Math.min(plan, meta);
});

const isExceedingLimits = computed(() => {
  if (!limitsState.data) return false;
  if (remainingMessages.value === Infinity) return false;
  return limitsState.audienceCount > remainingMessages.value;
});

const isSubmitDisabled = computed(
  () => v$.value.$invalid || !hasRequiredTemplateParams.value || isExceedingLimits.value || limitsState.isFetchingAudience || limitsState.audienceCount === 0
);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, initialState);
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => {
  // Find the selected template to get its content
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;

  // Extract template content - this should be the template message body
  const templateContent = parserData?.renderedTemplate || '';

  // Prepare template_params object with the same structure as used in contacts
  const templateParams = {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || 'UTILITY',
    language: currentTemplate?.language || 'en_US',
    processed_params: parserData?.processedParams || {},
  };

  return {
    title: state.title,
    message: templateContent,
    template_params: templateParams,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience: state.selectedAudience?.map(id => ({
      id,
      type: 'Label',
    })),
    trigger_rules: {
      delay_between_messages: state.delayBetweenMessages,
    },
  };
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

// Reset template selection when inbox changes
watch(
  () => state.inboxId,
  async (newInboxId) => {
    state.templateId = null;
    if (newInboxId) {
      limitsState.isLoading = true;
      try {
        const limits = await store.dispatch('campaigns/fetchLimits', newInboxId);
        limitsState.data = limits;
      } catch (error) {
        // ignore
      } finally {
        limitsState.isLoading = false;
      }
    }
  }
);

watch(
  () => state.selectedAudience,
  async (newAudience) => {
    if (!newAudience || newAudience.length === 0) {
      limitsState.audienceCount = 0;
      return;
    }
    limitsState.isFetchingAudience = true;
    try {
      const labelTitles = newAudience
        .map(id => formState.labels.value.find(l => l.id === id)?.title)
        .filter(Boolean);

      if (labelTitles.length === 0) return;

      // Assuming the first label in the selection is what we estimate on.
      // (Campaigns currently support one audience label practically for WhatsApp)
      const audienceLabel = labelTitles[0];
      const response = await CampaignAPI.estimate(audienceLabel);
      
      limitsState.audienceCount = response.data.valid_contacts;
      limitsState.omittedCount = response.data.omitted_contacts;
    } catch (error) {
      // ignore
    } finally {
      limitsState.isFetchingAudience = false;
    }
  },
  { deep: true }
);
const handleBuyMore = () => {
  const accountId = store.getters.getCurrentAccountId;
  if (router) {
    router.push(`/app/accounts/${accountId}/settings/billing`);
  } else {
    window.location.href = `/app/accounts/${accountId}/settings/billing`;
  }
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <WhatsAppCampaignLimitsWidget
      v-if="state.inboxId"
      :is-loading="limitsState.isLoading"
      :plan-available="limitsState.data.plan_available"
      :meta-daily-limit="limitsState.data.meta_daily_limit"
      :sends-today="limitsState.data.sends_today"
      :remaining-today="limitsState.data.remaining_today"
    />

    <div class="flex flex-col gap-1">
      <label for="template" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        id="template"
        v-model="state.templateId"
        :options="templateOptions"
        :has-error="!!formErrors.template"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
        :message="formErrors.template"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
      </p>
    </div>

    <!-- Template Parser -->
    <WhatsAppTemplateParser
      v-if="selectedTemplate"
      ref="templateParserRef"
      :template="selectedTemplate"
    />

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
      <div v-if="limitsState.isFetchingAudience" class="text-xs text-n-slate-11 mt-1">
        Calculando audiencia...
      </div>
      <div v-else-if="limitsState.audienceCount === 0" class="text-xs mt-1 text-n-ruby-11 font-medium">
        Audiencia estimada: 0 contactos (Ningún contacto válido encontrado).
      </div>
      <div v-else class="text-xs mt-1" :class="isExceedingLimits ? 'text-n-ruby-11 font-medium' : 'text-n-slate-11'">
        Audiencia estimada: {{ limitsState.audienceCount }} contactos.
        <span v-if="limitsState.omittedCount > 0" class="text-n-ruby-9 ml-1">
          (Omitidos: {{ limitsState.omittedCount }})
        </span>
        <div v-if="isExceedingLimits" class="mt-2 p-3 bg-n-ruby-2 border border-n-ruby-3 rounded-lg flex flex-col gap-2">
          <span class="font-bold text-n-ruby-11 flex items-center gap-1">
            <span class="i-lucide-alert-triangle w-4 h-4"></span>
            ¡Límite excedido por {{ limitsState.audienceCount - remainingMessages }} mensajes!
          </span>
          <span class="text-n-ruby-11">
            Tu límite actual disponible es de {{ remainingMessages }} mensajes.
          </span>
          <button type="button" @click="handleBuyMore" class="mt-1 self-start inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-n-ruby-9 hover:bg-n-ruby-10 rounded-md transition-colors cursor-pointer border-0">
            <span class="i-lucide-shopping-cart w-3 h-3"></span> Adquirir paquete adicional
          </button>
        </div>
      </div>
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <Input
      v-model="state.delayBetweenMessages"
      label="Retraso entre mensajes (segundos)"
      type="number"
      min="0"
      placeholder="Ej: 3"
      :message="formErrors.delayBetweenMessages"
    />

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
